const buildApiBaseUrl = () => {
  const rawUrl = process.env.REACT_APP_CARDS_API_URL || "";
  if (!rawUrl) return "";
  return rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
};

const apiBaseUrl = buildApiBaseUrl();

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

const jsonHeaders = {
  "Content-Type": "application/json",
};

const parseJsonOrThrow = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || `Richiesta fallita (${response.status}).`);
    error.statusCode = response.status;
    error.payload = data;
    throw error;
  }
  return data;
};

export const fetchAvatarAiThemes = async ({ token }) => {
  const res = await fetch(`${apiBaseUrl}/self/avatar-ai/themes`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseJsonOrThrow(res);
};

export const fetchAvatarAiCredits = async ({ token }) => {
  const res = await fetch(`${apiBaseUrl}/self/avatar-ai/credits`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseJsonOrThrow(res);
};

export const requestAvatarAiSourceUploadUrl = async ({ token, file }) => {
  const res = await fetch(`${apiBaseUrl}/self/avatar-ai/source-upload-url`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      fileName: file.name || `capture-${Date.now()}.jpg`,
      contentType: file.type || "image/jpeg",
      size: file.size,
    }),
  });
  return parseJsonOrThrow(res);
};

export const uploadAvatarAiSource = async ({ uploadDescriptor, file, onProgress }) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(uploadDescriptor.method || "PUT", uploadDescriptor.uploadUrl);
    Object.entries(uploadDescriptor.headers || {}).forEach(([k, v]) =>
      xhr.setRequestHeader(k, v),
    );
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ key: uploadDescriptor.key });
      } else {
        reject(new Error(`Upload fallito (${xhr.status}).`));
      }
    };
    xhr.onerror = () => reject(new Error("Errore di rete durante upload."));
    xhr.send(file);
  });
};

export const submitAvatarAiJob = async ({
  token,
  themeId,
  sourceType,
  sourceKey,
  modelId,
}) => {
  const res = await fetch(`${apiBaseUrl}/self/avatar-ai/jobs`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      themeId,
      sourceType,
      ...(sourceType === "uploaded" ? { sourceKey } : {}),
      ...(modelId ? { modelId } : {}),
    }),
  });
  return parseJsonOrThrow(res);
};

export const pollAvatarAiJob = async ({ token, jobId }) => {
  const res = await fetch(
    `${apiBaseUrl}/self/avatar-ai/jobs/${encodeURIComponent(jobId)}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return parseJsonOrThrow(res);
};

export const fetchAvatarAiAvatars = async ({ token }) => {
  const res = await fetch(`${apiBaseUrl}/self/avatar-ai/avatars`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  return parseJsonOrThrow(res);
};

export const applyAvatarAiJob = async ({ token, jobId }) => {
  const res = await fetch(
    `${apiBaseUrl}/self/avatar-ai/jobs/${encodeURIComponent(jobId)}/apply`,
    {
      method: "POST",
      headers: authHeaders(token),
    },
  );
  return parseJsonOrThrow(res);
};

export const fetchEventAvatarAiThemes = async () => {
  const res = await fetch(`${apiBaseUrl}/event/avatar-ai/themes`, {
    method: "GET",
  });
  return parseJsonOrThrow(res);
};

export const requestEventAvatarAiSourceUploadUrl = async ({
  sessionId,
  file,
}) => {
  const res = await fetch(`${apiBaseUrl}/event/avatar-ai/source-upload-url`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({
      sessionId,
      fileName: file.name || `capture-${Date.now()}.jpg`,
      contentType: file.type || "image/jpeg",
      size: file.size,
    }),
  });
  return parseJsonOrThrow(res);
};

export const submitEventAvatarAiJob = async ({
  sessionId,
  themeId,
  sourceKey,
  modelId,
}) => {
  const res = await fetch(`${apiBaseUrl}/event/avatar-ai/jobs`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({
      sessionId,
      themeId,
      sourceType: "uploaded",
      sourceKey,
      ...(modelId ? { modelId } : {}),
    }),
  });
  return parseJsonOrThrow(res);
};

export const pollEventAvatarAiJob = async ({ sessionId, jobId }) => {
  const params = new URLSearchParams();
  if (sessionId) params.set("sessionId", sessionId);
  const query = params.toString();
  const res = await fetch(
    `${apiBaseUrl}/event/avatar-ai/jobs/${encodeURIComponent(jobId)}${
      query ? `?${query}` : ""
    }`,
    {
      method: "GET",
    },
  );
  return parseJsonOrThrow(res);
};
